import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';


/**
 * Sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 * Supports both the custom pdp-variant-configurator and
 * Horizon's standard variant picker.
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {AbortController | null} */
  #stepperAbort = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #hasConfigurator = false;

  /** @type {boolean} */
  #configuratorResolved = true;

  connectedCallback() {
    super.connectedCallback();

    // Detect if the PDP variant configurator is present
    const section = this.closest('.shopify-section');
    this.#hasConfigurator = !!section?.querySelector('pdp-variant-configurator');

    // If configurator is present, hide the entire sticky bar until a variant is resolved
    if (this.#hasConfigurator) {
      this.#configuratorResolved = false;
    }

    this.#setupIntersectionObserver();
    this.#setupQtyStepper();
    this.#setupQtySync(section);

    const { signal } = this.#abortController;

    // Listen for Horizon's variant events (non-configurator products)
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    // Listen for configurator events
    document.addEventListener('configurator:variant-resolved', this.#handleConfiguratorUpdate, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddSuccess, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddError, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#stepperAbort?.abort();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return;

    const buyButtonsBlock =
      sectionElement.querySelector('pdp-variant-configurator .pdp-configurator__actions') ??
      sectionElement.querySelector('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }
  }

  #setStickyQtyDisabled(disabled) {
    const minusBtn = this.querySelector('[data-sticky-qty-minus]');
    const plusBtn = this.querySelector('[data-sticky-qty-plus]');
    const input = this.refs.stickyQtyInput;
    if (minusBtn) minusBtn.disabled = disabled;
    if (plusBtn) plusBtn.disabled = disabled;
    if (input) input.disabled = disabled;
  }

  /**
   * Sets up the quantity stepper buttons
   */
  #setupQtyStepper() {
    this.#stepperAbort?.abort();
    this.#stepperAbort = new AbortController();
    const { signal } = this.#stepperAbort;

    const minusBtn = this.querySelector('[data-sticky-qty-minus]');
    const plusBtn = this.querySelector('[data-sticky-qty-plus]');
    const input = this.refs.stickyQtyInput;
    if (!input) return;

    const updateMinusState = () => {
      if (minusBtn) minusBtn.disabled = (parseInt(input.value, 10) || 1) <= 1;
    };

    minusBtn?.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      if (val > 1) input.value = val - 1;
      this.#currentQuantity = parseInt(input.value, 10) || 1;
      updateMinusState();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }, { signal });

    const max = parseInt(input.max, 10) || 99;

    plusBtn?.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      if (val < max) input.value = val + 1;
      this.#currentQuantity = parseInt(input.value, 10) || 1;
      updateMinusState();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }, { signal });

    input.addEventListener('change', () => {
      let val = parseInt(input.value, 10) || 1;
      if (val < 1) val = 1;
      if (val > max) val = max;
      input.value = val;
      this.#currentQuantity = val;
      updateMinusState();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }, { signal });
  }

  /** @type {boolean} */
  #syncing = false;

  /**
   * Bidirectional quantity sync between sticky bar and configurator
   */
  #setupQtySync(section) {
    if (!section) return;
    const { signal } = this.#abortController;
    const stickyInput = this.refs.stickyQtyInput;
    const configInput = section.querySelector('pdp-variant-configurator [data-qty-input]');
    if (!stickyInput || !configInput) return;

    // Configurator → Sticky
    configInput.addEventListener('input', () => {
      if (this.#syncing) return;
      this.#syncing = true;
      const val = parseInt(configInput.value, 10) || 1;
      stickyInput.value = val;
      this.#currentQuantity = val;
      this.#syncing = false;
    }, { signal });

    // Sticky → Configurator
    stickyInput.addEventListener('input', () => {
      if (this.#syncing) return;
      this.#syncing = true;
      const val = parseInt(stickyInput.value, 10) || 1;
      configInput.value = val;
      this.#syncing = false;
    }, { signal });
  }

  // Public action handlers
  handleAddToCartClick = async () => {
    const btn = this.refs.addToCartButton;
    const textEl = btn.querySelector('.add-to-cart-text__content');

    // If variant is unavailable, navigate to dealer page instead of adding to cart
    if (this.dataset.variantAvailable === 'false' && this.dataset.currentVariantId) {
      const configurator = this.closest('.shopify-section')?.querySelector('pdp-variant-configurator');
      const dealerUrl = configurator?.dataset.dealerUrl || '/apps/store-locator';
      window.location.href = dealerUrl;
      return;
    }

    if (!this.#targetAddToCartButton) {
      // Try to find the configurator form button as fallback
      const section = this.closest('.shopify-section');
      const configBtn = section?.querySelector('pdp-variant-configurator [data-add-to-cart]');
      if (configBtn) {
        // Sync quantity to configurator
        const configQtyInput = section.querySelector('pdp-variant-configurator [data-qty-input]');
        if (configQtyInput) configQtyInput.value = this.#currentQuantity;
        this.#showAddingState();
        configBtn.click();
        return;
      }
      return;
    }

    // Sync quantity to the main product form before clicking
    const productForm = this.#getProductForm();
    const mainQtyInput = productForm?.querySelector('[name="quantity"]');
    if (mainQtyInput) mainQtyInput.value = this.#currentQuantity;

    this.#showAddingState();
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  #showAddingState() {
    const btn = this.refs.addToCartButton;
    const textEl = btn.querySelector('.add-to-cart-text__content');
    btn.disabled = true;
    if (textEl) textEl.textContent = this.dataset.tAdding || 'Adding\u2026';
  }

  #showAddedState() {
    const btn = this.refs.addToCartButton;
    const textEl = btn.querySelector('.add-to-cart-text__content');
    if (textEl) textEl.textContent = this.dataset.tAdded || 'Added!';
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      btn.disabled = false;
      if (textEl) textEl.textContent = this.dataset.tAddToCart || 'Add to Cart';
    }, 1500);
  }

  #showErrorState(message) {
    const btn = this.refs.addToCartButton;
    const textEl = btn.querySelector('.add-to-cart-text__content');
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    btn.disabled = false;
    if (textEl) textEl.textContent = this.dataset.tAddToCart || 'Add to Cart';
    if (message && window.PdpUtils?.showErrorTooltip) {
      window.PdpUtils.showErrorTooltip(message, btn);
    }
  }

  /**
   * Handles configurator variant resolved/deselected events
   */
  #handleConfiguratorUpdate = (event) => {
    const { variant, sku, price, available } = event.detail;
    const infoRow = this.refs.infoRow;
    const modelName = this.refs.modelName;
    const priceDisplay = this.refs.priceDisplay;
    const btn = this.refs.addToCartButton;
    const textEl = btn.querySelector('.add-to-cart-text__content');

    if (variant) {
      // Variant resolved — show sticky bar, show info row, enable button
      this.#configuratorResolved = true;
      if (infoRow) infoRow.style.display = '';
      if (modelName) modelName.textContent = sku || variant.title || '';
      if (priceDisplay) priceDisplay.textContent = price != null ? window.PdpUtils?.formatMoney(price) || '' : '';

      this.dataset.variantAvailable = String(available);
      this.dataset.currentVariantId = String(variant.id);

      if (available) {
        btn.disabled = false;
        if (textEl) textEl.textContent = this.dataset.tAddToCart || 'Add to Cart';
        this.#setStickyQtyDisabled(false);
      } else {
        btn.disabled = false;
        if (textEl) textEl.textContent = this.dataset.tFindDealer || 'Find a Dealer';
        this.#setStickyQtyDisabled(true);
      }

      // Re-cache target button — configurator form button
      const section = this.closest('.shopify-section');
      const configBtn = section?.querySelector('pdp-variant-configurator [data-add-to-cart]');
      if (configBtn) this.#targetAddToCartButton = configBtn;
    } else {
      // Variant deselected — hide the entire sticky bar
      this.#configuratorResolved = false;
      this.#hideStickyBar();
      if (infoRow) infoRow.style.display = 'none';
      this.dataset.variantAvailable = 'false';
      this.dataset.currentVariantId = '';
      btn.disabled = true;
      if (textEl) textEl.textContent = this.dataset.tSelectOptions || 'Select Options';
    }
  };

  /**
   * Handles Horizon variant update events (non-configurator products)
   */
  #handleVariantUpdate = (event) => {
    if (this.#hasConfigurator) return;
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.dataset.currentVariantId = '';
    }

    // Re-bind qty stepper after morph
    this.#setupQtyStepper();
  };

  /**
   * Handles Horizon variant selected events
   */
  #handleVariantSelected = (event) => {
    if (this.#hasConfigurator) return;
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Handles cart add success
   */
  #handleCartAddSuccess = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    this.#showAddedState();
  };

  /**
   * Handles cart add error
   */
  #handleCartAddError = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    const message = event.detail?.data?.message || event.detail?.data?.description;
    if (this.#isStuck) {
      this.#showErrorState(message);
    }
  };

  #showStickyBar() {
    if (this.#hasConfigurator && !this.#configuratorResolved) return;
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
